<template>
  <SkDialog
    v-model="showDialog"
    width="490px"
    :title="$t('config.title')"
    @update:modelValue="emit('update:modelValue', false)"
   data-testid="components-modals-config-sk-dialog">
    <template #content>
      <div class="p-5" data-testid="components-modals-config-div">
        <SkSelect
          v-model="timezone" 
          :label="$t('config.label')"
          :placeholder="$t('config.placeholder')"
          :items="allTimezones"
          item-title="label"
          item-value="value"
        data-testid="components-modals-config-sk-select" />
        <p
          class="config__body-current mt-1"
          @click="useCurrentTimezone"
         data-testid="components-modals-config-p">
          {{ $t('config.current') }}
        </p>
        <div class="flex flex-col gap-2 bg-[#0000000D] p-4 mt-4 rounded-[16px]" data-testid="components-modals-config-div-2">
           <div class="flex justify-between items-center border-b border-[#0000001A] pb-2" data-testid="components-modals-config-div-3">
             <span class="text-m font-medium text-[#00000099]" data-testid="components-modals-config-span">{{ $t('config.help') }}</span>
             <SkSwitch v-model="isHelp" data-testid="components-modals-config-sk-switch" />
           </div>
           <span class="text-xs text-[#00000099]" data-testid="components-modals-config-span-2">{{ $t('config.help_description') }}</span>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="p-5 flex gap-3" data-testid="components-modals-config-div-4">
        <SkModalActionButton
          variant="cancel"
          height="38px"
          width="100%"
          @click="closeDialog"
         data-testid="components-modals-config-sk-modal-action-button">
          {{ $t('cancel') }}
        </SkModalActionButton>
        <SkModalActionButton
          height="38px"
          width="100%"
          @click="saveTimezoneHandler"
         data-testid="components-modals-config-sk-modal-action-button-2">
          {{ $t('config.save') }}
        </SkModalActionButton>
      </div>
    </template>
  </SkDialog>
</template>

<script setup>
import {defineEmits, defineProps, computed, ref, onMounted, watch} from "vue";

import SkDialog from "@/components/ui/SkDialog/SkDialog.vue";
import SkSwitch from "@/components/ui/SkSwitch/SkSwitch.vue";
import UiDialog from '@/components/ui/dialog.vue';
import ModalHeader from '@/components/modals/components/header.vue';
import UiSelect from '@/components/ui/select.vue';
import UiButton from '@/components/ui/button.vue';
import UiTooltip from '@/components/ui/tooltip.vue'

import { allTimezones, getTimezone } from "@/utils/helpers/timezone.js";
import { userStore } from "@/store/modules/user.js";
import { useI18n } from 'vue-i18n';
import { useHelpSettings } from '@/composables/useHelpSettings.js';

import axiosInstance from '@/utils/axios';
import requestLogger from '@/utils/requestLogger';
import SkSelect from "../ui/SkSelect/SkSelect.vue";
import SkModalActionButton from '@/components/ui-new/SkModalActionButton.vue';

const UserStore = userStore();
const { t } = useI18n();
const { isHideHelp, hideHelpGlobally, showHelpGlobally } = useHelpSettings();

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
});
const emit = defineEmits(['update:modelValue']);

const showDialog = computed({
  get: () => props.modelValue,
  set(newVal) {
    emit('update:modelValue', newVal)
  }
});
const isHelp = ref(!isHideHelp.value);
const timezone = ref(null)

const useCurrentTimezone = ()=>{
  const peace = getTimezone()
  timezone.value = { value: peace, label: peace }
}

onMounted(() => {
  if (!UserStore.timezone_key) {
    const detected = getTimezone()
    UserStore.SET_TIMEZONE_KEY(detected)
  }
  timezone.value = { value: UserStore.timezone_key, label: UserStore.timezone_key }
})

watch(() => props.modelValue, (isOpen) => {
  if (isOpen) {
    isHelp.value = !isHideHelp.value;
  }
});

const closeDialog = () => {
  emit('update:modelValue', false);
}

const saveTimezoneHandler = async ()=>{
  UserStore.SET_TIMEZONE_KEY(timezone.value.value);
  if (isHelp.value) {
    await showHelpGlobally();
  } else {
    await hideHelpGlobally();
  }
  emit('update:modelValue', false);
  //requestLogger.retryRequests(axiosInstance);
}
</script>

<style lang="scss" scoped>
:deep(.sk-select) {
  background-color: #FFF;
  border: 1px solid #0000001A;
  border-radius: 18px;
}
:deep(.sk-select__inner) {
  color: #00000099;
}
:deep(.sk-select__label) {
  font-weight: 500;
  color: #00000033;
}
.config {
  padding: 18px 0 0;
  height: 100%;
  max-height: 235px;
  background: #FFF;
  border-radius: 12px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.06), 0 8px 40px rgba(0, 0, 0, 0.04);
  box-sizing: border-box;

  &__header {
    padding: 0 20px;
    box-sizing: border-box;
  }

  &__body {
    display: flex;
    flex-direction: column;
    row-gap: 4px;
    padding: 16px 20px;

    &-current{
      cursor: pointer;
      margin-left: 10px;
      font-size: 12px;
      font-weight: 400;
      line-height: 14.4px;
      text-align: left;
      text-decoration: underline;
      color: #00000066;
    }
  }

  &__footer{
    padding: 12px 20px 20px;

    &-button{
      font-size: 14px;
      font-weight: 600;
      line-height: 19px;
      text-align: left;
    }
  }

  .divider {
    margin-left: -20px;
    width: calc(100% + 40px);
    height: 1px;
    background: #CDD4DD;
  }
}
</style>
